import React, {useCallback} from 'react';
import {Button, Grid, Stack} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import {useVisitReportStore} from "../../api/VisitReportStore";
import {Page, StorePageCount} from "../../components/page/Page";
import {LoadingData} from "../../components/loading-data/LoadingData";
import {useDeleteAction} from "../../util/actions/DeleteAction";
import {VisitReportCard} from "./VisitReportCard";
import {useMessage} from "../../util/actions/Message";
import {useVisitWithStatStore} from "../../api/VisitWithStatStore";
import {downloadVisitReportCsv} from "./ExportVisitReportCsv";
import {usePermission} from "../../util/access/UseAccess";
import {VisitReports_Admin} from "../../util/access/Permission";
import {UploadBerlinVisitReportButton} from "./UploadBerlinVisitReportButton";
import {ImportPragueVisitReportsButton} from "./ImportPragueVisitReportsButton";
import {downloadWeeklyVisitReportCsv} from "./ExportWeeklyVisitReportCsv";
import {UploadAmsterdamVisitReportButton} from "./UploadAmsterdamVisitReportButton";

export default function VisitReportPage() {
    const {deleteReport} = useVisitReportStore();
    const reports = useVisitWithStatStore(store => store.remoteValue);
    const message = useMessage();
    const {canAdmin} = usePermission({canAdmin: VisitReports_Admin});
    const handleDeleteReport = useDeleteAction({
        description: 'Are you sure you want to delete this visit report? This action cannot be undone.',
        successMessage: 'Deleted visit report',
        failureMessage: 'Failed to delete visit report',
        action: deleteReport,
    }, [deleteReport]);

    const handleExportCsv = useCallback(async () => {
        const rows = await reports.value;
        if (rows.length > 0) {
            downloadVisitReportCsv(rows);
        } else {
            message.error('No visit reports to export.');
        }
    }, [reports, message]);

    const handleWeeklyReport = useCallback(async () => {
        const rows = await reports.value;
        if (rows.length > 0) {
            downloadWeeklyVisitReportCsv(rows);
        } else {
            message.error('No visit reports to export.');
        }
    }, [reports, message]);

    return (
        <Page
            header="Visit Reports"
            counter={<StorePageCount value={reports}/>}
            actions={
                <Stack direction="row" gap={2}>
                    {canAdmin && (
                        <>
                            <UploadBerlinVisitReportButton/>
                            <UploadAmsterdamVisitReportButton/>
                            <ImportPragueVisitReportsButton/>
                        </>
                    )}
                    <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<DownloadIcon/>}
                        onClick={handleExportCsv}
                    >
                        Monthly
                    </Button>
                    <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<DownloadIcon/>}
                        onClick={handleWeeklyReport}
                    >
                        Weekly
                    </Button>
                </Stack>
            }
        >
            <LoadingData
                value={reports}
                emptyMessage={"No visit reports available yet."}
                defaultErrorMessage="Failed to load visit reports. Please try again later."
            >{reports => (
                <Grid container rowSpacing={2} flexDirection="column">
                    {reports.map(report => (
                        <VisitReportCard
                            key={report.id}
                            report={report}
                            onDeleteReport={canAdmin ? handleDeleteReport : undefined}
                        />
                    ))}
                </Grid>
            )}</LoadingData>
        </Page>
    );
}
